// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import { Badge, Flex, Stack, Text, Space } from '@mantine/core';

import type { RenderableSpool } from 'components/Autodesk/extensions/SpoolsExtension/types';
import { GridMenu, type GridMenuProps } from 'components/Mantine/GridMenu';

import { VisibilityToggle } from './VisibilityToggle';

type Props<T extends string> = {
  spool: RenderableSpool;
  isVisible: boolean;
  toggleVisibility: () => void;
  /* when set to true, the visibility toggle will be disabled */
  disableVisibility?: boolean;
  leftSection?: ReactNode;
  beforeTitle?: ReactNode;
  /** When true, displays a "Fabrication Items" badge indicating the spool has associated fabrication elements. */
  hasFabricationElements?: boolean;
  /* If provided, a `GridMenu` component will be rendered with these props */
  menuProps?: GridMenuProps<T>;
};

/**
 * Visual representation of a Spool from ForgeViewerFromModel.
 * Includes visibility controls and an optional menu.
 */
export const SpoolCard = <T extends string = ''>({
  spool,
  isVisible,
  toggleVisibility,
  disableVisibility = false,
  leftSection,
  beforeTitle,
  hasFabricationElements = false,
  menuProps,
}: Props<T>) => (
  <Flex p="md" justify="space-between" align="center" gap="xs" mih={100} className="selectable-spool">
    <Flex align="center" gap="md">
      {leftSection ?? <Space />}
      <Stack gap={0}>
        <Flex align="center" gap="xs">
          {beforeTitle}
          <Text title={spool.name}>{spool.name}</Text>

          {spool.spoolStatus && (
            <Flex flex="0 0 auto">
              <Badge size="sm" tt="none" color="gray.7" radius="md" title={spool.spoolStatus}>
                {spool.spoolStatus}
              </Badge>
            </Flex>
          )}
          {hasFabricationElements && (
            <Flex flex="0 0 auto">
              <Badge size="sm" tt="none" color="teal.7" radius="md" title="This spool has fabrication items">
                Fabrication Items
              </Badge>
            </Flex>
          )}
        </Flex>
        {(spool.level || spool.location) && (
          <Text c="dimmed" fz="sm">
            Location: {spool.level}
            {spool.level && spool.location ? <> &bull; </> : null}
            {spool.location}
          </Text>
        )}
        {(spool.package || spool.packageStatus) && (
          <Text c="dimmed" fz="sm">
            Package: {spool.package} {spool.packageStatus && <>({spool.packageStatus})</>}
          </Text>
        )}
      </Stack>
    </Flex>
    <Flex gap="xs" align="center">
      <VisibilityToggle disabled={disableVisibility} visible={isVisible} onToggle={toggleVisibility} />
      {menuProps && <GridMenu {...menuProps} />}
    </Flex>
  </Flex>
);
